/**
 * Project integration bindings — shared types for the project-level
 * "which external resource belongs to which project" layer (migration 159).
 *
 * Vocabulary (mirrors the UI copy):
 *   - Connection      = tenant-level access to an external system
 *                       (external_integrations / devops_integrations / …).
 *   - Project binding = an external resource linked to ONE project for ONE
 *                       purpose. This module.
 *   - Routing         = rules that decide where approvals/notifications go
 *                       (governance routes first, then bindings).
 *
 * The unions below are mirrored by CHECK constraints in migration 159 —
 * keep both in sync (constraint-drift convention, see migration 157).
 */

export const PROJECT_BINDING_PROVIDERS = [
  "slack",
  "teams",
  "freshdesk",
  "odoo",
  "topdesk",
  "jira",
  "linear",
  "github",
  "gitlab",
  "bitbucket",
  "kubernetes",
  "argo",
  "email",
  "webhook",
] as const;
export type ProjectBindingProvider = (typeof PROJECT_BINDING_PROVIDERS)[number];

export const PROJECT_BINDING_REF_TYPES = [
  "slack_channel",
  "slack_user_group",
  "teams_channel",
  "freshdesk_group",
  "freshdesk_product",
  "freshdesk_tag",
  // Catch-all: every ticket from the tenant's Freshdesk connection that no
  // group/product/tag binding claims. Lowest routing precedence.
  "freshdesk_account",
  // Odoo Helpdesk team (helpdesk.team id) — the routing unit tickets land in.
  "odoo_team",
  // TOPdesk operator group (UUID) — the routing unit incidents land in.
  "topdesk_operator_group",
  "jira_project",
  "jira_component",
  "linear_team",
  "linear_project",
  "github_repo",
  "gitlab_repo",
  "bitbucket_repo",
  "kubernetes_cluster",
  "kubernetes_namespace",
  "kubernetes_service",
  "kubernetes_label_selector",
  "argo_application",
  "argo_rollout",
  "argo_project",
  "argo_namespace",
  "email_alias",
  "webhook_endpoint",
] as const;
export type ProjectBindingRefType = (typeof PROJECT_BINDING_REF_TYPES)[number];

/**
 * Kubernetes ref types whose binding is scoped to a SPECIFIC cluster. The
 * single-owner invariant (migration 162) keys on (provider, ref_type, ref_id),
 * so the same namespace/service/label in two different clusters would collide
 * to one owner. To let two clusters' `payments` namespace belong to different
 * projects, the cluster id is folded into the external_ref_id for these types.
 * `kubernetes_cluster` itself is NOT here — it already identifies a cluster.
 */
export const CLUSTER_SCOPED_REF_TYPES: readonly ProjectBindingRefType[] = [
  "kubernetes_namespace",
  "kubernetes_service",
  "kubernetes_label_selector",
];

/** Delimiter between the cluster id and the resource value in a cluster-scoped
 *  external_ref_id. `::` cannot occur in a Kubernetes namespace/service name
 *  (RFC 1123 labels) so the encoding is unambiguous. */
export const K8S_CLUSTER_REF_DELIM = "::";

/**
 * Build the external_ref_id for a (possibly cluster-scoped) Kubernetes binding.
 * With a clusterRef the id is `<clusterRef>::<value>`; without one it stays the
 * bare value (legacy / any-cluster binding). Used by BOTH the binding store (on
 * create) and the inbound resolver (on match) so the two always agree. The
 * value is matched, never parsed apart, so cluster ids that themselves contain
 * `:` are safe.
 */
export function kubernetesBindingRefId(value: string, clusterRef?: string | null): string {
  const v = value.trim();
  const c = clusterRef?.trim();
  return c ? `${c}${K8S_CLUSTER_REF_DELIM}${v}` : v;
}

/** Strip a cluster prefix back off a cluster-scoped ref id, for display only. */
export function kubernetesBindingDisplayValue(refId: string): string {
  const i = refId.indexOf(K8S_CLUSTER_REF_DELIM);
  return i >= 0 ? refId.slice(i + K8S_CLUSTER_REF_DELIM.length) : refId;
}

export const PROJECT_BINDING_PURPOSES = [
  "intake",
  "approvals",
  "notifications",
  "status",
  "delivery",
  "deployment",
  "incidents",
  "code",
] as const;
export type ProjectBindingPurpose = (typeof PROJECT_BINDING_PURPOSES)[number];

/** Which ref types make sense for which provider — used by API validation
 *  and by the UI to scope the type dropdown per provider card. */
export const REF_TYPES_BY_PROVIDER: Record<
  ProjectBindingProvider,
  readonly ProjectBindingRefType[]
> = {
  slack: ["slack_channel", "slack_user_group"],
  teams: ["teams_channel"],
  freshdesk: ["freshdesk_group", "freshdesk_product", "freshdesk_tag", "freshdesk_account"],
  odoo: ["odoo_team"],
  topdesk: ["topdesk_operator_group"],
  jira: ["jira_project", "jira_component"],
  linear: ["linear_team", "linear_project"],
  github: ["github_repo"],
  gitlab: ["gitlab_repo"],
  bitbucket: ["bitbucket_repo"],
  kubernetes: [
    "kubernetes_cluster",
    "kubernetes_namespace",
    "kubernetes_service",
    "kubernetes_label_selector",
  ],
  argo: ["argo_application", "argo_rollout", "argo_project", "argo_namespace"],
  email: ["email_alias"],
  webhook: ["webhook_endpoint"],
};

/**
 * Providers whose `approvals` binding actually DELIVERS approval requests —
 * the Slack and Teams approval cards (governance/approval-delivery.ts). An
 * older e-mail `approvals` binding was never read by any delivery (approval
 * e-mail goes to the people who can decide), and an Argo one configures a
 * deployment promotion, not a place requests are sent. Counts and readiness
 * use this so such a binding never reads as "approval channel set up".
 */
export const APPROVAL_DELIVERY_PROVIDERS: readonly ProjectBindingProvider[] = ["slack", "teams"];

export function deliversApprovalRequests(b: { provider: ProjectBindingProvider; purpose: string }): boolean {
  return b.purpose === "approvals" && APPROVAL_DELIVERY_PROVIDERS.includes(b.provider);
}

/** Which purposes make sense for which provider — the single source of
 *  truth for API validation AND the UI's "Use for" checkboxes. The UI
 *  renders these through provider-aware labels (intake = "New issues" on
 *  Slack, "New tickets" on Freshdesk) via the i18n purposeLabels map.
 *
 *  Freshdesk deliberately has NO approvals purpose: Freshdesk mirrors
 *  progress/status and is not an approval channel unless that capability
 *  is explicitly built. Same reasoning per provider below. */
export const PURPOSES_BY_PROVIDER: Record<
  ProjectBindingProvider,
  readonly ProjectBindingPurpose[]
> = {
  slack: ["intake", "approvals", "notifications", "status", "incidents"],
  teams: ["intake", "approvals", "notifications", "status"],
  freshdesk: ["intake", "status", "notifications"],
  // Odoo mirrors like Freshdesk: a helpdesk is intake + status/progress.
  odoo: ["intake", "status", "notifications"],
  // TOPdesk mirrors like Freshdesk/Odoo: a service desk is intake +
  // status/progress. No approvals — an approval channel needs an explicit
  // capability, and TOPdesk has no interactive approval surface.
  topdesk: ["intake", "status", "notifications"],
  // Issue trackers mirror like Freshdesk: intake + status/progress. No
  // approvals by design — approval channels need an explicit capability.
  jira: ["intake", "status", "notifications"],
  linear: ["intake", "status", "notifications"],
  github: ["code", "delivery", "notifications"],
  gitlab: ["code", "delivery", "notifications"],
  bitbucket: ["code", "delivery", "notifications"],
  kubernetes: ["deployment", "status", "incidents"],
  argo: ["deployment", "approvals", "status"],
  // No approvals: approval e-mail goes to the PEOPLE who can decide, each
  // with their own single-use links (governance/approval-delivery.ts) — a
  // shared mailbox cannot carry that, and nothing ever read such a binding.
  email: ["intake", "notifications"],
  webhook: ["intake", "deployment", "notifications", "incidents", "delivery"],
};

/**
 * The single, stable external_ref_id for a Freshdesk catch-all binding.
 *
 * A tenant has one Freshdesk connection, so a fixed sentinel — rather than
 * a domain/connection id that both the picker and the webhook would have to
 * derive and normalise identically — is the robust key. ref_type
 * `freshdesk_account` + this id means "every ticket from this tenant's
 * Freshdesk that no group/product/tag binding claims". The single-owner
 * trigger (migration 162) guarantees only one project can own it.
 */
export const FRESHDESK_ACCOUNT_CATCHALL_REF = "*";

/** Deployment environment tag stored in binding config.environment. */
export const BINDING_ENVIRONMENTS = ["development", "staging", "production"] as const;
export type BindingEnvironment = (typeof BINDING_ENVIRONMENTS)[number];

export type BindingValidationInput = {
  provider: ProjectBindingProvider;
  externalRefType: ProjectBindingRefType;
  externalRefId: string;
  purpose: ProjectBindingPurpose;
  config?: Record<string, unknown>;
};

export type BindingValidationResult =
  | { ok: true }
  | { ok: false; code: string; message: string };

/** owner/repo — both segments non-empty, no extra slashes or whitespace. */
const REPO_FULL_NAME_RE = /^[^\s/]+\/[^\s/]+$/;
/** GitLab allows nested subgroups: group/subgroup/…/project (≥2 segments). */
const GITLAB_PATH_RE = /^[^\s/]+(\/[^\s/]+)+$/;

/**
 * Provider-aware binding validation shared by the create and update API
 * routes. Pure so it is unit-testable; the zod schemas guarantee enum
 * membership and string bounds BEFORE this runs — this layer owns the
 * cross-field rules a flat schema can't express.
 */
export function validateBindingForProvider(
  input: BindingValidationInput,
): BindingValidationResult {
  const { provider, externalRefType, externalRefId, purpose } = input;
  if (!REF_TYPES_BY_PROVIDER[provider].includes(externalRefType)) {
    return {
      ok: false,
      code: "invalid_ref_type",
      message: `externalRefType ${externalRefType} is not valid for provider ${provider}`,
    };
  }
  if (!PURPOSES_BY_PROVIDER[provider].includes(purpose)) {
    return {
      ok: false,
      code: "invalid_purpose",
      message: `purpose ${purpose} is not valid for provider ${provider}`,
    };
  }
  if (externalRefId.trim().length === 0) {
    return { ok: false, code: "invalid_ref_id", message: "externalRefId must not be blank" };
  }
  // GitHub/Bitbucket are strictly owner/repo; GitLab paths may nest
  // subgroups (group/subgroup/project), so ≥2 segments is the rule there.
  const repoRe =
    externalRefType === "gitlab_repo"
      ? GITLAB_PATH_RE
      : externalRefType === "github_repo" || externalRefType === "bitbucket_repo"
        ? REPO_FULL_NAME_RE
        : null;
  if (repoRe && !repoRe.test(externalRefId)) {
    return {
      ok: false,
      code: "invalid_repo_format",
      message:
        externalRefType === "gitlab_repo"
          ? "Repository reference must use the group/project format (subgroups allowed)"
          : "Repository reference must use the owner/repo format",
    };
  }
  const config = input.config ?? {};
  if (
    config.environment !== undefined &&
    !BINDING_ENVIRONMENTS.includes(config.environment as BindingEnvironment)
  ) {
    return {
      ok: false,
      code: "invalid_environment",
      message: `config.environment must be one of: ${BINDING_ENVIRONMENTS.join(", ")}`,
    };
  }
  if (
    config.promotionApprovals !== undefined &&
    typeof config.promotionApprovals !== "boolean"
  ) {
    return {
      ok: false,
      code: "invalid_config",
      message: "config.promotionApprovals must be a boolean",
    };
  }
  if (config.labelSelector !== undefined && typeof config.labelSelector !== "string") {
    return {
      ok: false,
      code: "invalid_config",
      message: "config.labelSelector must be a string",
    };
  }
  // Auto-open deployment-gate intent (Argo deployment bindings). When the
  // toggle is on, the mission's release step opens an Argo-bound deployment
  // gate after the CTO patch gate — so it needs a concrete rollout target.
  if (config.autoDeployGate !== undefined && typeof config.autoDeployGate !== "boolean") {
    return { ok: false, code: "invalid_config", message: "config.autoDeployGate must be a boolean" };
  }
  for (const k of ["namespace", "rollout"] as const) {
    if (config[k] !== undefined && typeof config[k] !== "string") {
      return { ok: false, code: "invalid_config", message: `config.${k} must be a string` };
    }
  }
  if (config.autoDeployGate === true) {
    if (provider !== "argo") {
      return { ok: false, code: "invalid_config",
        message: "config.autoDeployGate is only valid on an argo binding" };
    }
    const ns = typeof config.namespace === "string" ? config.namespace.trim() : "";
    const ro = typeof config.rollout === "string" ? config.rollout.trim() : "";
    if (!ns || !ro) {
      return { ok: false, code: "invalid_config",
        message: "config.namespace and config.rollout are required when autoDeployGate is enabled" };
    }
  }
  // Auto code-delivery (git bindings): after a patch is approved, FixControl
  // applies it + opens a PR on the bound repo. Only meaningful on a git host.
  if (config.autoDeliver !== undefined && typeof config.autoDeliver !== "boolean") {
    return { ok: false, code: "invalid_config", message: "config.autoDeliver must be a boolean" };
  }
  if (config.autoDeliver === true && !GIT_BINDING_PROVIDERS.includes(provider)) {
    return { ok: false, code: "invalid_config",
      message: `config.autoDeliver is only valid on a git binding (${GIT_BINDING_PROVIDERS.join(", ")})` };
  }
  const jiraImport = validateJiraTicketImportConfig(provider, config);
  if (!jiraImport.ok) return jiraImport;
  return { ok: true };
}

/* ── Jira ticket import (opt-in per intake binding) ──────────────────── */

/** Most labels / issue types one import filter may list. */
export const JIRA_IMPORT_FILTER_MAX_ITEMS = 20;
const JIRA_IMPORT_FILTER_MAX_LEN = 100;

/**
 * What a Jira `intake` binding says about importing new Jira tickets as
 * FixControl issues, read off its `config`:
 *
 *   config.importTickets  boolean — the opt-in. Anything but `true` is off,
 *                         so every binding that existed before this switch
 *                         keeps importing nothing.
 *   config.importFilter   optional { labels?: string[]; issueTypes?: string[] }
 *                         — a ticket must carry at least one listed label
 *                         AND be one of the listed issue types (each list
 *                         only applies when it is non-empty; compared
 *                         case-insensitively).
 *
 * Pure, so the webhook importer, the binding validation and the UI read the
 * same answer.
 */
export type JiraTicketImportSettings = {
  enabled: boolean;
  labels: string[];
  issueTypes: string[];
};

function cleanFilterList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((s): s is string => typeof s === "string")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, JIRA_IMPORT_FILTER_MAX_ITEMS);
}

export function jiraTicketImportSettings(
  config: Record<string, unknown> | null | undefined,
): JiraTicketImportSettings {
  const filter = config?.importFilter;
  const f = filter && typeof filter === "object" && !Array.isArray(filter)
    ? (filter as Record<string, unknown>)
    : {};
  return {
    enabled: !!config && config.importTickets === true,
    labels: cleanFilterList(f.labels),
    issueTypes: cleanFilterList(f.issueTypes),
  };
}

function validateJiraTicketImportConfig(
  provider: ProjectBindingProvider,
  config: Record<string, unknown>,
): BindingValidationResult {
  if (config.importTickets === undefined && config.importFilter === undefined) return { ok: true };
  if (provider !== "jira") {
    return { ok: false, code: "invalid_config",
      message: "config.importTickets and config.importFilter are only valid on a jira binding" };
  }
  if (config.importTickets !== undefined && typeof config.importTickets !== "boolean") {
    return { ok: false, code: "invalid_config", message: "config.importTickets must be a boolean" };
  }
  if (config.importFilter === undefined) return { ok: true };
  const f = config.importFilter;
  if (!f || typeof f !== "object" || Array.isArray(f)) {
    return { ok: false, code: "invalid_config", message: "config.importFilter must be an object" };
  }
  for (const [k, v] of Object.entries(f as Record<string, unknown>)) {
    if (k !== "labels" && k !== "issueTypes") {
      return { ok: false, code: "invalid_config",
        message: "config.importFilter only supports labels and issueTypes" };
    }
    const okList = Array.isArray(v)
      && v.length <= JIRA_IMPORT_FILTER_MAX_ITEMS
      && v.every((s) => typeof s === "string" && s.trim().length > 0 && s.length <= JIRA_IMPORT_FILTER_MAX_LEN);
    if (!okList) {
      return { ok: false, code: "invalid_config",
        message: `config.importFilter.${k} must be a list of at most ${JIRA_IMPORT_FILTER_MAX_ITEMS} non-empty names` };
    }
  }
  return { ok: true };
}

/** Git-host providers that support automatic patch delivery (apply → PR). */
export const GIT_BINDING_PROVIDERS: readonly ProjectBindingProvider[] = [
  "github",
  "gitlab",
  "bitbucket",
];

/** Whether a git binding's config opts into automatic code delivery (apply +
 *  PR on patch approval). Pure — shared by the resolver + binding validation. */
export function autoDeliverEnabled(
  config: Record<string, unknown> | null | undefined,
): boolean {
  return !!config && config.autoDeliver === true;
}

/** Per-project auto-deploy intent read off an Argo deployment binding's
 *  `config`. The mission's release step uses this to open an Argo-bound
 *  deployment gate (namespace/rollout) the operator approves to resume the
 *  paused rollout. */
export type AutoDeployTarget = {
  namespace: string;
  rollout: string;
  environment?: string;
};

/** May a resource of this provider be bound to a project, given the tenant's
 *  connection status?
 *
 *  Binding is CONFIGURATION, not a live call: it only needs a tenant
 *  credential to exist (`configured`). `connected` additionally requires the
 *  capability health to be fresh and green — it drives the green badge, and it
 *  flips to false on a failing probe, a dead-lettered delivery, or simply a
 *  health row older than the staleness TTL. Gating a resource picker on
 *  `connected` therefore makes the picker vanish on a perfectly usable
 *  integration, so pickers must gate on `configured` only.
 *
 *  A null/absent status means "not loaded yet" — stay permissive, the picker's
 *  own load state reports any real failure. */
export function canBindProvider(
  status: { configured: boolean } | null | undefined,
): boolean {
  return status == null || status.configured;
}

/** Resolve the auto-open deployment target from a binding's config, or null
 *  when the toggle is off or the rollout target is incomplete. Pure — shared by
 *  the planner (whether to add the release step) and the release leaf (which
 *  rollout to bind the gate to). */
export function autoDeployTargetFromConfig(
  config: Record<string, unknown> | null | undefined,
): AutoDeployTarget | null {
  if (!config || config.autoDeployGate !== true) return null;
  const namespace = typeof config.namespace === "string" ? config.namespace.trim() : "";
  const rollout = typeof config.rollout === "string" ? config.rollout.trim() : "";
  if (!namespace || !rollout) return null;
  const environment = typeof config.environment === "string" ? config.environment : undefined;
  return { namespace, rollout, environment };
}

export type ProjectIntegrationBinding = {
  id: string;
  tenant: string;
  projectId: string;
  provider: ProjectBindingProvider;
  /** Tenant-level connection row this binding rides on, when known.
   *  Informational pointer (not an FK) — see migration 159. */
  integrationId?: string;
  externalRefType: ProjectBindingRefType;
  /** Provider-native id: Slack channel id, Freshdesk group id, repo full
   *  name (owner/repo), namespace name, Argo application name, … */
  externalRefId: string;
  /** Human label (#payments-platform, "Payments namespace"). */
  externalRefName?: string;
  purpose: ProjectBindingPurpose;
  /** Provider-specific extras (environment, labelSelector, clusterId, …). */
  config: Record<string, unknown>;
  isDefault: boolean;
  enabled: boolean;
  /** Outbound-only: this binding mirrors/delivers FROM the project TO the
   *  resource, but never claims inbound ownership of it. Lets several projects
   *  share one external resource for outbound (migration 183). Default false —
   *  a normal binding is bidirectional and subject to the single-owner rule. */
  outboundOnly: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
};
